/**
 * Sätze zu den Codes der Preisautomatik: Grund eines Tagespreises (`DailyPriceReasonCodes`), Grund
 * des Mindestaufenthalts (`MinimumStayReasonCodes`), Beiträge (`PriceRuleContributionCodes`) und
 * Hinweise je Tag (`PricingEligibilityWarningCodes`, Belegungs- und Feiertagshinweise). Ein
 * unbekannter Code bleibt als Code sichtbar.
 */
const REASON: Record<string, string> = {
  price_changed: 'Neuer Preis berechnet.',
  below_minimum_delta: 'Änderung unter der Mindeständerung – der Preis bleibt.',
  unsupported_currency: 'Die Währung wird für automatische Preise nicht unterstützt.',
  invalid_price_anchors: 'Mindest-, Referenz- oder Maximalpreis fehlen oder passen nicht zusammen.',
  missing_confirmed_state: 'Für diesen Tag fehlt ein von Channex bestätigter Stand.',
  invalid_confirmed_state: 'Der bestätigte Stand ist ungültig.',
  invalid_calculation_time: 'Der Zeitpunkt der Berechnung ist ungültig.',
  invalid_version_state: 'Die Einstellungen sind in einem ungültigen Stand.',
  stale_configuration_version: 'Die Einstellungen haben sich während der Berechnung geändert.',
  stale_critical_state: 'Der bestätigte Stand ist nicht mehr aktuell.',
  missing_rule_contributions: 'Die Beiträge der Regeln fehlen.',
  eligibility_date_mismatch: 'Der Tag passt nicht zur Prüfung, ob er berechnet werden darf.',
  day_ineligible: 'Dieser Tag wird nicht automatisch berechnet.',
  booked_day: 'Gebucht – das Angebot ist nicht frei.',
  operator_closed_day: 'Von Hand gesperrt – die Automatik lässt den Tag aus.',
  pending_hold_day: 'Eine Buchung wartet noch auf Bestätigung.',
  ambiguous_day: 'Der Buchungsstand des Tages ist nicht eindeutig.',
  movement_hard_bounds_conflict: 'Die Bewegungsgrenze widerspricht Mindest- oder Maximalpreis.',
  inconsistent_eligibility: 'Die Angaben zum Tag widersprechen sich.',
  rate_not_owned: 'Den Preis setzt jemand von Hand – die Automatik lässt ihn stehen.',
};

const MINIMUM_STAY: Record<string, string> = {
  baseline_applied: 'Regulärer Mindestaufenthalt.',
  gap_applied: 'Lücke füllen: kürzerer Mindestaufenthalt.',
  lead_time_tier_applied: 'Vorlaufstaffel greift.',
  day_ineligible: 'Dieser Tag wird nicht automatisch berechnet.',
  max_stay_conflict:
    'Höchstaufenthalt ist kleiner als der Mindestaufenthalt – es gilt die engere Grenze.',
};

export const CONTRIBUTION: Record<string, string> = {
  weekday_holiday: 'Wochentag oder Feiertag',
  far_out: 'Weit im Voraus',
  occupancy: 'Belegung',
  direct_last_minute: 'Last Minute',
  gap: 'Lücke',
  booking_recency: 'Letzte Buchungen',
};

const WARNING: Record<string, string> = {
  ambiguous_booking_state: 'Buchungsstand nicht eindeutig.',
  booking_block_overlap: 'Buchung überschneidet sich mit einer Sperre.',
  booking_pending_overlap: 'Buchung überschneidet sich mit einer offenen Anfrage.',
  manual_rate_ownership: 'Preis von Hand gesetzt – die Automatik lässt ihn aus.',
  manual_minimum_stay_ownership:
    'Mindestaufenthalt von Hand gesetzt – die Automatik lässt ihn aus.',
  ota_pending_hold: 'Ein Kanal hält den Tag für eine offene Buchung.',
  sold_night_operator_closed: 'Verkaufte Nacht ist zugleich gesperrt.',
  pending_booking_requires_review: 'Eine offene Buchung muss geprüft werden.',
  missing_occupancy_night: 'Für eine Nacht fehlt der Belegungsstand.',
  duplicate_occupancy_night: 'Eine Nacht ist doppelt im Belegungsstand.',
  unknown_occupancy_warning: 'Unbekannter Hinweis zur Belegung.',
  holiday_location_unavailable: 'Kein Standort am Objekt – Feiertage fließen nicht ein.',
  holiday_subdivision_review_required: 'Bundesland ungeklärt – Feiertage fließen nicht ein.',
  holiday_calendar_cache_missing: 'Feiertagskalender nicht geladen – Feiertage fließen nicht ein.',
  pricing_calendar_contribution_data_invalid: 'Die gespeicherten Beiträge sind nicht lesbar.',
  pricing_calendar_warning_data_invalid: 'Die gespeicherten Hinweise sind nicht lesbar.',
};

const lookup = (table: Record<string, string>) => (code: string) => table[code] ?? null;

/** Satz zum Grund eines Preises; `null`, wenn der Code unbekannt ist. */
export const reasonText = lookup(REASON);
export const minimumStayText = lookup(MINIMUM_STAY);
export const warningCodeText = lookup(WARNING);
export const contributionLabel = (code: string) => CONTRIBUTION[code] ?? code;
